import { RequireAuth, RequireNoAuth, RequireVerification } from '@psyomics/ui-common';
import {
  BackgroundLayout,
  BreadcrumbRouteConfig,
  ConfirmForgotPassword,
  ForgotPassword,
  GenericError,
} from '@psyomics/ui-components';
import { Route, Routes } from 'react-router-dom';

import { PublicLayout } from '../shared/components/public-layout/public-layout';
import { SecureLayout } from '../shared/components/secure-layout/secure-layout';

import { REFERRAL_REDIRECT_WHITELIST } from './redirect-whitelists';
import { Account } from './routes/account/account';
import { ChangePassword } from './routes/account/change-password';
import { ChangePasswordSuccess } from './routes/account/change-password-success';
import { ForgotPasswordSuccess } from './routes/forgot-password/success';
import { Home } from './routes/home/home';
import { MfaSetup } from './routes/mfa-setup/mfa-setup';
import { RequireMfaSetup } from './routes/mfa-setup/require-mfa-setup';
import { ProfessionalReferral } from './routes/professional-referral/professional-referral';
import { ReferralRequestCreate } from './routes/referral-request/referral-request-create';
import { ReferralRequestEdit } from './routes/referral-request/referral-request-edit';
import { ReferralRequestSubmit } from './routes/referral-request/referral-request-submit';
import { ReferralRequestSurvey } from './routes/referral-request/referral-request-survey';
import { ReferralRequestView } from './routes/referral-request/referral-request-view';
import { AccountVerification } from './routes/verification/account-verification';
import { SelfHostedLogin } from './self-hosted-login';
import { SelfHostedRegister } from './self-hosted-register';

const getIndexRoute = () => '/';
const getLoginRoute = () => '/login';
const forgotPasswordRoute = '/forgot-password';

const unauthenticatedRoutes = (
  <Route Component={PublicLayout}>
    <Route Component={ProfessionalReferral} path="/professional-referral/:orgCode" />
    <Route Component={SelfHostedRegister} path="/register" />
    <Route Component={ForgotPassword} path={forgotPasswordRoute} />
    <Route Component={ConfirmForgotPassword} path={`${forgotPasswordRoute}/confirm`} />
    <Route Component={ForgotPasswordSuccess} path={`${forgotPasswordRoute}/success`} />
    <Route element={<SelfHostedLogin forgotPasswordUrl={forgotPasswordRoute} />} path="/login" />
    <Route element={<GenericError titleKey="common.errors.notFound" />} path="/*"></Route>
  </Route>
);

const authenticatedRoutes = (
  <Route element={<RequireVerification redirectWhitelist={REFERRAL_REDIRECT_WHITELIST} requireEmail />}>
    <Route Component={AccountVerification} path="account-verification" />
    <Route element={<RequireMfaSetup />}>
      <Route Component={SecureLayout}>
        <Route element={<BreadcrumbRouteConfig crumb={{ labelKey: 'navigation.home', href: '/' }} />}>
          <Route Component={Home} index />
          <Route path="referral-request">
            <Route
              element={
                <BreadcrumbRouteConfig
                  crumb={{ labelKey: 'page.professionalReferral.breadcrumb.createReferralRequest' }}
                />
              }
            >
              <Route Component={ReferralRequestCreate} path=":orgCode/:serviceCode/create" />
            </Route>
            <Route path=":id">
              <Route
                element={
                  <BreadcrumbRouteConfig
                    crumb={{
                      labelKey: 'page.professionalReferral.breadcrumb.editReferralRequest',
                      href: '/referral-request/:id/edit',
                    }}
                  />
                }
              >
                <Route Component={ReferralRequestEdit} path="edit" />
                <Route
                  element={
                    <BreadcrumbRouteConfig crumb={{ labelKey: 'page.professionalReferral.breadcrumb.questionnaire' }} />
                  }
                >
                  <Route Component={ReferralRequestSurvey} path="survey" />
                </Route>
              </Route>
              <Route Component={ReferralRequestView} path="view" />
              <Route element={<ReferralRequestSurvey reviewMode />} path="survey/view" />
              <Route Component={ReferralRequestSubmit} path="submit" />
            </Route>
          </Route>
          <Route Component={MfaSetup} path="mfa-setup" />
          <Route Component={Account} path="account" />
          <Route Component={ChangePassword} path="account/change-password" />
          <Route Component={ChangePasswordSuccess} path="account/change-password-success" />
          <Route element={<GenericError titleKey="common.errors.notFound" />} path="/*"></Route>
        </Route>
      </Route>
    </Route>
  </Route>
);

const AuthRoutes = () => (
  <Routes>
    <Route Component={BackgroundLayout}>
      <Route element={<RequireNoAuth getIndexRoute={getIndexRoute} />}>{unauthenticatedRoutes}</Route>
      <Route element={<RequireAuth getLoginRoute={getLoginRoute} />}>{authenticatedRoutes}</Route>
    </Route>
  </Routes>
);

export { AuthRoutes };
